import { useState } from 'react';
import { Box, Link, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export interface RegistroAfectado {
    tipo: string;
    id?: number;
    etiqueta: string;
}

export type OrigenErrorSvcc = 'SVCC' | 'RAR' | 'ESTABLECIMIENTO' | 'SISTEMA';

export interface ErrorSvcc {
    codigo: string;
    anexo: string;
    campo: string;
    /** Dónde se corrige el dato. Puede faltar en respuestas viejas cacheadas. */
    origen?: OrigenErrorSvcc;
    mensajeUsuario: string;
    comoResolver: string;
    severidad: 'error' | 'advertencia';
    registros: RegistroAfectado[];
    mensajeOriginal?: string;
}

interface Props {
    errores: ErrorSvcc[];
}

const COLOR_ERROR = '#DC2626';
const COLOR_ADVERTENCIA = '#B45309';

/**
 * Dónde va el usuario a corregir el dato. El anexo dice qué parte de la DDJJ
 * falla, pero no en qué pantalla se carga: un mismo Anexo I mezcla datos del
 * establecimiento con datos del informe SVCC, y un Anexo III mezcla la nómina
 * del RAR con las exposiciones.
 *
 * `SISTEMA` no lleva chip: no hay pantalla adónde mandar al usuario y una
 * etiqueta ahí sólo agrega ruido.
 */
const ORIGEN: Record<string, { etiqueta: string; color: string; fondo: string }> = {
    SVCC: { etiqueta: 'SVCC', color: '#0E7490', fondo: '#CFFAFE' },
    RAR: { etiqueta: 'RAR', color: '#7C3AED', fondo: '#EDE9FE' },
    ESTABLECIMIENTO: { etiqueta: 'Establecimiento', color: '#0F766E', fondo: '#CCFBF1' }
};

/**
 * Cuántos registros afectados se muestran antes de colapsar. Un hallazgo de
 * nómina puede arrastrar decenas de trabajadores: listarlos todos convierte el
 * detalle en un muro de CUILs donde no se distingue un hallazgo del siguiente.
 */
const REGISTROS_VISIBLES = 5;

const RegistrosAfectados = ({ registros }: { registros: RegistroAfectado[] }) => {
    const [expandido, setExpandido] = useState(false);
    const hayDeMas = registros.length > REGISTROS_VISIBLES;
    const visibles = expandido ? registros : registros.slice(0, REGISTROS_VISIBLES);

    return (
        <Box component="ul" sx={{ m: '4px 0 0', pl: 2, listStyle: 'none' }}>
            {visibles.map((registro) => (
                <Typography
                    key={registro.etiqueta}
                    component="li"
                    variant="caption"
                    sx={{ color: '#475569', fontFamily: 'monospace', display: 'block' }}
                >
                    {registro.etiqueta}
                </Typography>
            ))}
            {hayDeMas && (
                <Box component="li">
                    <Link
                        component="button"
                        type="button"
                        variant="caption"
                        underline="hover"
                        onClick={() => setExpandido((previo) => !previo)}
                    >
                        {expandido
                            ? 'Ver menos'
                            : `Ver los ${registros.length - REGISTROS_VISIBLES} restantes`}
                    </Link>
                </Box>
            )}
        </Box>
    );
};

export default function HallazgosEstablecimiento({ errores }: Props) {
    if (!errores.length) return null;

    // Los bloqueantes primero: son los que impiden enviar. Las advertencias
    // mezcladas entre medio hacen que se pierdan de vista.
    const ordenados = [...errores].sort((a, b) => {
        if (a.severidad === b.severidad) return 0;
        return a.severidad === 'error' ? -1 : 1;
    });

    return (
        <Box display="flex" flexDirection="column" gap={0.75} sx={{ mt: 0.5 }}>
            {ordenados.map((hallazgo, index) => {
                const esError = hallazgo.severidad === 'error';
                const color = esError ? COLOR_ERROR : COLOR_ADVERTENCIA;
                const origen = hallazgo.origen ? ORIGEN[hallazgo.origen] : undefined;

                return (
                    <Box
                        key={`${hallazgo.codigo}-${index}`}
                        display="flex"
                        alignItems="flex-start"
                        gap={0.75}
                    >
                        <InfoOutlinedIcon
                            sx={{ fontSize: 14, mt: '2px', flexShrink: 0, color }}
                        />
                        <Box>
                            <Typography variant="caption" sx={{ color }}>
                                {origen && (
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-block',
                                            mr: 0.75,
                                            px: 0.65,
                                            py: '1px',
                                            borderRadius: '3px',
                                            bgcolor: origen.fondo,
                                            color: origen.color,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            letterSpacing: '.03em',
                                            verticalAlign: '1px'
                                        }}
                                    >
                                        {origen.etiqueta}
                                    </Box>
                                )}
                                <strong>{`Anexo ${hallazgo.anexo} · ${hallazgo.campo}: `}</strong>
                                {`${hallazgo.mensajeUsuario} ${hallazgo.comoResolver}`}
                                <Box component="span" sx={{ ml: 1, fontWeight: 600 }}>
                                    {esError ? 'Bloquea' : 'Advertencia'}
                                </Box>
                            </Typography>
                            {hallazgo.mensajeOriginal && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#475569',
                                        fontFamily: 'monospace',
                                        display: 'block'
                                    }}
                                >
                                    {hallazgo.mensajeOriginal}
                                </Typography>
                            )}
                            {hallazgo.registros.length > 0 && (
                                <RegistrosAfectados registros={hallazgo.registros} />
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
